<template>
  <div
    :class="[
      'rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md',
      server.is_active
        ? 'border-l-4 border-l-green-500 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
        : 'border-l-4 border-l-gray-300 dark:border-l-gray-600 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-75',
    ]"
    @click="emit('click')"
  >
    <div class="space-y-3">
      <div class="flex items-start justify-between">
        <div class="min-w-0 flex-1">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {{ server.display_name }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
            /{{ server.slug }}/mcp
          </p>
        </div>
        <span
          :class="[
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            server.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
          ]"
        >
          {{ server.is_active ? 'Active' : 'Inactive' }}
        </span>
      </div>

      <div v-if="server.description" class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
        {{ server.description }}
      </div>

      <div class="flex items-center justify-between">
        <div class="flex flex-wrap gap-1">
          <span
            v-for="cap in activeCapabilities"
            :key="cap"
            class="px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
          >
            {{ cap }}
          </span>
        </div>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ mappingCount }} tool{{ mappingCount === 1 ? '' : 's' }}
        </span>
      </div>

      <div class="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" size="sm" @click.stop="emit('manage-tools')">Tools</Button>
        <Button
          :variant="server.is_active ? 'outline' : 'primary'"
          size="sm"
          @click.stop="emit('toggle-active')"
        >
          {{ server.is_active ? 'Disable' : 'Enable' }}
        </Button>
        <Button variant="outline" size="sm" @click.stop="emit('edit')">Edit</Button>
        <Button variant="danger" size="sm" @click.stop="emit('delete')">Delete</Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Button } from '@asabdev/asab-ui';
  import type { VirtualServer } from '@/lib/mcp/types';

  interface Props {
    server: VirtualServer;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'click'): void;
    (e: 'edit'): void;
    (e: 'delete'): void;
    (e: 'manage-tools'): void;
    (e: 'toggle-active'): void;
  }>();

  const activeCapabilities = computed(() => {
    const caps = props.server.capabilities;
    if (!caps) return [];
    const result: string[] = [];
    if (caps.tools) result.push('tools');
    if (caps.resources) result.push('resources');
    if (caps.prompts) result.push('prompts');
    if (caps.sampling) result.push('sampling');
    if (caps.elicitation) result.push('elicitation');
    if (caps.tasks) result.push('tasks');
    return result;
  });

  const mappingCount = computed(() => props.server.tool_mappings?.length ?? 0);
</script>
